/*!
  _   _  ___  ____  ___ ________  _   _   _   _ ___   ____  ____   ___  
 | | | |/ _ \|  _ \|_ _|__  / _ \| \ | | | | | |_ _| |  _ \|  _ \ / _ \ 
 | |_| | | | | |_) || |  / / | | |  \| | | | | || |  | |_) | |_) | | | |
 |  _  | |_| |  _ < | | / /| |_| | |\  | | |_| || |  |  __/|  _ <| |_| |
 |_| |_|\___/|_| \_\___/____\___/|_| \_|  \___/|___| |_|   |_| \_\\___/ 
                                                                                                                                                                                                                                                                                                                                       
=========================================================
* Horizon UI Dashboard PRO - v1.0.0
=========================================================

* Product Page: https://www.horizon-ui.com/pro/
* Copyright 2022 Horizon UI (https://www.horizon-ui.com/)

* Designed and Coded by Simmmple

=========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.

*/

import React from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
// Chakra imports
import {
  Box,
  Button,
  Checkbox,
  Flex,
  FormControl,
  FormLabel,
  Heading,
  Icon,
  Input,
  InputGroup,
  InputRightElement,
  Text,
  useColorModeValue,
  useToast,
} from '@chakra-ui/react';
// Custom components
import { HSeparator } from 'components/separator/Separator';
import DefaultAuth from 'layouts/auth/variants/Default';
import { useAuth } from 'contexts/AuthContext';
import { GoogleLogin, CredentialResponse } from '@react-oauth/google';
// Assets
import illustration from 'assets/img/auth/auth.png';
import { FcGoogle } from 'react-icons/fc';
import { MdOutlineRemoveRedEye } from 'react-icons/md';
import { RiEyeCloseLine } from 'react-icons/ri';

// Use Niko solar panel background
const nikoLoginBackground = '/Login.png';

function SignIn() {
  // Niko color scheme - black text on white background with yellow accent
  const textColor = '#000000'; // Niko black
  const textColorSecondary = 'gray.600';
  const textColorDetails = '#000000';
  const textColorBrand = '#000000'; // Niko black for links
  const brandStars = '#EBFD03'; // Niko yellow for required asterisk
  const googleBg = 'white';
  const googleText = '#000000';
  const googleHover = { bg: 'gray.50' };
  const googleActive = { bg: 'gray.100' };

  // State
  const [show, setShow] = React.useState(false);
  const [username, setUsername] = React.useState('');
  const [password, setPassword] = React.useState('');
  const [isLoading, setIsLoading] = React.useState(false);

  // Hooks
  const { login, loginWithGoogle } = useAuth();
  const navigate = useNavigate();
  const toast = useToast();

  const handleClick = () => setShow(!show);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!username || !password) {
      toast({
        title: 'Error',
        description: 'Please enter both username and password',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    setIsLoading(true);

    try {
      await login({ username, password });

      toast({
        title: 'Success',
        description: 'Logged in successfully',
        status: 'success',
        duration: 2000,
        isClosable: true,
      });
      navigate('/assessment-v2');
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.message || 'Login failed',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleSuccess = async (credentialResponse: CredentialResponse) => {
    if (!credentialResponse.credential) {
      toast({
        title: 'Error',
        description: 'No credential received from Google',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    setIsLoading(true);

    try {
      await loginWithGoogle(credentialResponse.credential);

      toast({
        title: 'Success',
        description: 'Logged in with Google successfully',
        status: 'success',
        duration: 2000,
        isClosable: true,
      });
      navigate('/assessment-v2');
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.message || 'Google login failed',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleError = () => {
    toast({
      title: 'Error',
      description: 'Google login failed. Please try again.',
      status: 'error',
      duration: 3000,
      isClosable: true,
    });
  };
  return (
    <DefaultAuth illustrationBackground={nikoLoginBackground} image={nikoLoginBackground}>
      <Flex
        maxW={{ base: '100%', md: 'max-content' }}
        w="100%"
        mx={{ base: 'auto', lg: '0px' }}
        ms={{ lg: '0px' }}
        me="auto"
        h="100%"
        alignItems="start"
        justifyContent="flex-start"
        mb={{ base: '30px', md: '60px' }}
        px={{ base: '25px', md: '0px' }}
        ps={{ lg: '0px' }}
        mt={{ base: '40px', md: '14vh' }}
        flexDirection="column"
      >
        <Box me="auto" mb="40px">
          <Heading 
            color={textColor} 
            fontSize={{ base: '32px', md: '42px' }} 
            mb="16px"
            fontWeight="600"
            lineHeight="1.2"
          >
            Bienvenidx al Roof Mapper AI
          </Heading>
          <Text
            ms="4px"
            color={textColorSecondary}
            fontWeight="400"
            fontSize="lg"
          >
            Transformando datos en energía solar
          </Text>
        </Box>
        <Flex
          zIndex="2"
          direction="column"
          w={{ base: '100%', md: '420px' }}
          maxW="100%"
          background="transparent"
          borderRadius="15px"
          mx={{ base: 'auto', lg: 'unset' }}
          me="auto"
          mb={{ base: '20px', md: 'auto' }}
        >
          <Box mb="26px">
            <GoogleLogin
              onSuccess={handleGoogleSuccess}
              onError={handleGoogleError}
              useOneTap
              text="signin_with"
              width="420"
            />
          </Box>
          <Flex align="center" mb="25px">
            <HSeparator />
            <Text color="gray.400" mx="14px">
              or
            </Text>
            <HSeparator />
          </Flex>
          <form onSubmit={handleSubmit}>
            <FormControl>
              <FormLabel
                display="flex"
                ms="4px"
                fontSize="sm"
                fontWeight="600"
                color={textColor}
                mb="8px"
              >
                Usuario<Text color={brandStars}>*</Text>
              </FormLabel>
              <Input
                isRequired={true}
                variant="auth"
                fontSize="sm"
                ms={{ base: '0px', md: '0px' }}
                type="text"
                placeholder="Ingresa tu usuario"
                mb="24px"
                fontWeight="500"
                size="lg"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                isDisabled={isLoading}
                borderColor="gray.300"
                _hover={{ borderColor: 'gray.400' }}
                _focus={{ borderColor: '#EBFD03', boxShadow: '0 0 0 1px #EBFD03' }}
              />
              <FormLabel
                ms="4px"
                fontSize="sm"
                fontWeight="600"
                color={textColor}
                display="flex"
              >
                Contraseña<Text color={brandStars}>*</Text>
              </FormLabel>
              <InputGroup size="md">
                <Input
                  isRequired={true}
                  fontSize="sm"
                  placeholder="Mín. 8 caracteres"
                  mb="24px"
                  size="lg"
                  type={show ? 'text' : 'password'}
                  variant="auth"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  isDisabled={isLoading}
                  borderColor="gray.300"
                  _hover={{ borderColor: 'gray.400' }}
                  _focus={{ borderColor: '#EBFD03', boxShadow: '0 0 0 1px #EBFD03' }}
                />
                <InputRightElement display="flex" alignItems="center" mt="4px">
                  <Icon
                    color={textColorSecondary}
                    _hover={{ cursor: 'pointer' }}
                    as={show ? RiEyeCloseLine : MdOutlineRemoveRedEye}
                    onClick={handleClick}
                  />
                </InputRightElement>
              </InputGroup>
              <Flex justifyContent="space-between" align="center" mb="24px">
                <FormControl display="flex" alignItems="center">
                  <Checkbox 
                    id="remember-login" 
                    me="10px"
                    borderColor="gray.400"
                    sx={{
                      '[data-checked]': {
                        bg: '#EBFD03',
                        borderColor: '#EBFD03',
                        color: '#000000',
                        _hover: {
                          bg: '#d4e803',
                          borderColor: '#d4e803',
                        }
                      }
                    }}
                  />
                  <FormLabel
                    htmlFor="remember-login"
                    mb="0"
                    fontWeight="normal"
                    color={textColor}
                    fontSize="sm"
                  >
                    Mantener sesión iniciada
                  </FormLabel>
                </FormControl>
                <NavLink to="/auth/forgot-password">
                  <Text
                    color={textColorBrand}
                    fontSize="sm"
                    fontWeight="500"
                    _hover={{ textDecoration: 'underline' }}
                  >
                    ¿Olvidaste tu contraseña?
                  </Text>
                </NavLink>
              </Flex>
              <Button
                type="submit"
                fontSize="sm"
                bg="#EBFD03"
                color="#000000"
                fontWeight="600"
                w="100%"
                h="50"
                mb="24px"
                isLoading={isLoading}
                loadingText="Iniciando sesión..."
                _hover={{
                  bg: '#d4e803',
                  transform: 'translateY(-2px)',
                  boxShadow: 'lg',
                }}
                _active={{
                  bg: '#bdd003',
                }}
                transition="all 0.2s"
              >
                Iniciar Sesión con Google
              </Button>
            </FormControl>
          </form>
          <Flex
            flexDirection="column"
            justifyContent="center"
            alignItems="start"
            maxW="100%"
            mt="0px"
          >
            <Text color={textColorDetails} fontWeight="400" fontSize="14px">
              ¿No tienes una cuenta?
              <NavLink to="/auth/sign-up">
                <Text
                  color={textColorBrand}
                  as="span"
                  ms="5px"
                  fontWeight="600"
                  _hover={{ textDecoration: 'underline' }}
                >
                  Regístrate
                </Text>
              </NavLink>
            </Text>
          </Flex>
        </Flex>
      </Flex>
    </DefaultAuth>
  );
}

export default SignIn;
